import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '../hooks/useAuth';
import { LoginRequest } from '../types/auth.types';
import { Input } from '../../common';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuth(navigate);
  const [credentials, setCredentials] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    if (!credentials.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!credentials.password) {
      errors.password = 'Password is required';
    } else if (credentials.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await login(credentials);
    } catch (error) {
      // Error is handled by the useAuth hook
      console.error('Login failed:', error);
    }
  };

  const handleInputChange = (field: keyof LoginRequest, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const header = (
    <div className="text-center py-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-3">Portal de Administración</h1>
      <p className="text-gray-600">Accede a tu panel de administración</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card header={header} className="shadow-2xl border-0">
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <Message 
                severity="error" 
                text={error} 
                className="w-full"
              />
            )}

            <Input
              id="email"
              label="Correo electrónico"
              type="email"
              value={credentials.email}
              onChange={(value) => handleInputChange('email', value)}
              placeholder="Ingresa tu correo electrónico"
              error={validationErrors.email}
              disabled={isLoading}
              required
            />

            <Input
              id="password"
              label="Contraseña"
              type="password"
              value={credentials.password}
              onChange={(value) => handleInputChange('password', value)}
              placeholder="Ingresa tu contraseña"
              error={validationErrors.password}
              disabled={isLoading}
              required
              showPasswordToggle
            />

            <Button
              type="submit"
              label={isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 border-0"
              disabled={isLoading}
            >
              {isLoading && <ProgressSpinner style={{ width: '20px', height: '20px' }} />}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                Acceso seguro a tus herramientas administrativas
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default LoginScreen; 